import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2, Eye, EyeOff } from "lucide-react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { PageLoader, Modal, useToast, useConfirm } from "@/components/ui";
import { Field, TextInput, Select } from "@/components/fields";
import type { CustomFieldDef } from "@shared/types";

const TYPES = ["text", "textarea", "number", "date", "select", "multiselect", "checkbox", "user", "url"];

export function AdminCustomFields() {
  const qc = useQueryClient();
  const toast = useToast();
  const { confirm, node } = useConfirm();
  const [entity, setEntity] = useState<"activity" | "project">("activity");
  const [creating, setCreating] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["custom-fields-all", entity],
    queryFn: () => api.get<{ fields: CustomFieldDef[] }>(`/custom-fields?entity=${entity}&all=1`),
  });
  const invalidate = () => qc.invalidateQueries({ queryKey: ["custom-fields"] });

  const upd = useMutation({ mutationFn: ({ id, ...b }: any) => api.put(`/custom-fields/${id}`, b), onSuccess: () => { invalidate(); qc.invalidateQueries({ queryKey: ["custom-fields-all"] }); } });
  const del = useMutation({
    mutationFn: ({ id, hard }: any) => api.del(`/custom-fields/${id}${hard ? "?hard=1" : ""}`),
    onSuccess: () => { invalidate(); qc.invalidateQueries({ queryKey: ["custom-fields-all"] }); toast.push("success", "Updated"); },
  });

  if (isLoading) return <PageLoader />;

  return (
    <>
      <PageHeader title="Custom Fields" subtitle="Add columns to the schedule or project record without touching code."
        actions={<button className="btn-primary" onClick={() => setCreating(true)}><Plus size={16} /> New field</button>} />

      <div className="mb-4 flex gap-1.5">
        {(["activity", "project"] as const).map((e) => (
          <button key={e} onClick={() => setEntity(e)}
            className={`rounded-lg px-3 py-1.5 text-sm capitalize ${entity === e ? "bg-brand-700 text-white" : "text-ink-soft hover:bg-surface-2"}`}>
            {e === "activity" ? "Activity / schedule fields" : "Project fields"}
          </button>
        ))}
      </div>

      <div className="card overflow-x-auto p-0">
        <table className="w-full text-sm">
          <thead><tr>{["Label", "Key", "Type", "Required", "In grid", "Active", ""].map((h) => <th key={h} className="th">{h}</th>)}</tr></thead>
          <tbody>
            {(data?.fields ?? []).map((f) => (
              <tr key={f.id} className="border-b border-line">
                <td className="td font-medium">{f.label}{f.helpText && <span className="block text-xs text-ink-faint">{f.helpText}</span>}</td>
                <td className="td text-xs text-ink-faint">{f.key}</td>
                <td className="td capitalize">{f.type}{f.options ? ` (${f.options.length})` : ""}</td>
                <td className="td"><input type="checkbox" checked={f.required} onChange={() => upd.mutate({ id: f.id, required: !f.required })} /></td>
                <td className="td"><input type="checkbox" checked={f.showInGrid} onChange={() => upd.mutate({ id: f.id, showInGrid: !f.showInGrid })} /></td>
                <td className="td">
                  <button className="btn-ghost !p-1" onClick={() => upd.mutate({ id: f.id, isActive: !f.isActive })}>
                    {f.isActive ? <Eye size={15} /> : <EyeOff size={15} className="text-ink-faint" />}
                  </button>
                </td>
                <td className="td">
                  <button className="btn-ghost !p-1 text-ink-faint hover:text-st-delayed"
                    onClick={async () => {
                      const hard = await confirm("Delete field", `Permanently delete "${f.label}" and all its stored values? Choose Cancel to just hide it instead.`, true);
                      del.mutate({ id: f.id, hard });
                    }}>
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
            ))}
            {!data?.fields.length && <tr><td className="td text-ink-faint" colSpan={7}>No custom fields yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {creating && <NewFieldModal entity={entity} onClose={() => { setCreating(false); invalidate(); qc.invalidateQueries({ queryKey: ["custom-fields-all"] }); }} />}
      {node}
    </>
  );
}

function NewFieldModal({ entity, onClose }: { entity: string; onClose: () => void }) {
  const toast = useToast();
  const [f, setF] = useState({ label: "", type: "text", required: false, helpText: "", showInGrid: true, optionsText: "" });
  const create = useMutation({
    mutationFn: () => api.post("/custom-fields", {
      entity, label: f.label, type: f.type, required: f.required, helpText: f.helpText, showInGrid: f.showInGrid,
      options: ["select", "multiselect"].includes(f.type) ? f.optionsText.split("\n").map((s) => s.trim()).filter(Boolean) : undefined,
    }),
    onSuccess: () => { toast.push("success", "Field created"); onClose(); },
    onError: (e: any) => toast.push("error", e?.message || "Failed"),
  });
  const needsOptions = ["select", "multiselect"].includes(f.type);
  return (
    <Modal open onClose={onClose} title={`New ${entity} field`}
      footer={<>
        <button className="btn-outline" onClick={onClose}>Cancel</button>
        <button className="btn-primary" disabled={!f.label || create.isPending} onClick={() => create.mutate()}>Create</button>
      </>}>
      <div className="space-y-3">
        <Field label="Label" required><TextInput value={f.label} onChange={(e) => setF({ ...f, label: e.target.value })} /></Field>
        <Field label="Type">
          <Select value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })}>
            {TYPES.map((t) => <option key={t} value={t} className="capitalize">{t}</option>)}
          </Select>
        </Field>
        {needsOptions && (
          <Field label="Options (one per line)"><textarea className="input min-h-[90px]" value={f.optionsText} onChange={(e) => setF({ ...f, optionsText: e.target.value })} /></Field>
        )}
        <Field label="Help text"><TextInput value={f.helpText} onChange={(e) => setF({ ...f, helpText: e.target.value })} /></Field>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.required} onChange={(e) => setF({ ...f, required: e.target.checked })} /> Required</label>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.showInGrid} onChange={(e) => setF({ ...f, showInGrid: e.target.checked })} /> Show as a column in the schedule grid</label>
      </div>
    </Modal>
  );
}
